import { defineStore } from 'pinia';
import api from '@/services/api';
import { useAuthStore } from './auth';

// Data is considered stale after 5 minutes
const STALE_TIME = 5 * 60 * 1000;

export const useReviewsStore = defineStore('reviews', {
	state: () => ({
		reviews: [],
		currentReview: null,
		loading: false,
		error: null,
		lastFetched: null
	}),

	getters: {
		allReviews: (state) => state.reviews,
		reviewById: (state) => (id) => state.reviews.find((review) => review.id === id),
		getCurrentReview: (state) => state.currentReview,
		isLoading: (state) => state.loading,
		hasError: (state) => !!state.error,
		getError: (state) => state.error,
		isStale: (state) => !state.lastFetched || Date.now() - state.lastFetched > STALE_TIME,
		hasData: (state) => state.reviews.length > 0,
		getAverageRating: (state) => (visitId) => {
			const visitReviews = state.reviews.filter((review) => review.visit?.id === visitId);
			if (visitReviews.length === 0) return 0;

			const sum = visitReviews.reduce((total, review) => total + review.rating, 0);
			return sum / visitReviews.length;
		},
		// Get the current user's review for a visit
		getUserReviewForVisit: (state) => (visitId) => {
			const authStore = useAuthStore();
			const userId = authStore.userId;
			return state.reviews.find((review) => {
				const reviewVisitId = review.visit?.id || review.visit;
				const reviewUserId = review.user?.id || review.user;
				return reviewVisitId === visitId && reviewUserId === userId;
			});
		}
	},

	actions: {
		setReviews(reviews) {
			this.reviews = reviews;
		},

		setCurrentReview(review) {
			this.currentReview = review;
		},

		setLoading(loading) {
			this.loading = loading;
		},

		setError(error) {
			this.error = error;
		},

		addReview(review) {
			this.reviews.push(review);
		},

		updateReview(updatedReview) {
			const index = this.reviews.findIndex((review) => review.id === updatedReview.id);
			if (index !== -1) {
				this.reviews.splice(index, 1, updatedReview);
			}
			if (this.currentReview && this.currentReview.id === updatedReview.id) {
				this.currentReview = updatedReview;
			}
		},

		removeReview(reviewId) {
			this.reviews = this.reviews.filter((review) => review.id !== reviewId);
			if (this.currentReview && this.currentReview.id === reviewId) {
				this.currentReview = null;
			}
		},

		async fetchReviews() {
			this.setLoading(true);
			this.setError(null);

			try {
				const response = await api.getReviews();
				this.setReviews(response.data);
				this.lastFetched = Date.now();
				return response.data;
			} catch (error) {
				this.setError(error.message || 'Failed to fetch reviews');
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		// Smart fetch - only fetches if data is missing or stale
		async ensureLoaded(force = false) {
			// Skip if already loading
			if (this.loading) return this.reviews;

			// Skip if we have fresh data and not forcing
			if (!force && this.hasData && !this.isStale) {
				return this.reviews;
			}

			return this.fetchReviews();
		},

		async fetchReview(reviewId) {
			this.setLoading(true);
			this.setError(null);

			try {
				const response = await api.getReview(reviewId);
				this.setCurrentReview(response.data);
				return response.data;
			} catch (error) {
				this.setError(error.message || `Failed to fetch review with ID ${reviewId}`);
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async createReview(reviewData) {
			this.setLoading(true);
			this.setError(null);

			try {
				const response = await api.createReview(reviewData);
				const newReview = response.data;
				this.addReview(newReview);
				return newReview;
			} catch (error) {
				this.setError(error.message || 'Failed to create review');
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async updateReviewAction({ id, reviewData }) {
			this.setLoading(true);
			this.setError(null);

			try {
				const response = await api.updateReview(id, reviewData);
				const updatedReview = response.data;
				this.updateReview(updatedReview);
				return updatedReview;
			} catch (error) {
				this.setError(error.message || `Failed to update review with ID ${id}`);
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async deleteReview(reviewId) {
			this.setLoading(true);
			this.setError(null);

			try {
				await api.deleteReview(reviewId);
				this.removeReview(reviewId);
			} catch (error) {
				this.setError(error.message || `Failed to delete review with ID ${reviewId}`);
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async fetchUserReviews() {
			this.setLoading(true);
			this.setError(null);

			try {
				const authStore = useAuthStore();
				const userId = authStore.userId;
				if (!userId) {
					throw new Error('User not authenticated');
				}

				const response = await api.getReviews();
				// Filter reviews by user
				const userReviews = response.data.filter((review) => review.user?.id === userId);
				this.setReviews(userReviews);
				return userReviews;
			} catch (error) {
				this.setError(error.message || 'Failed to fetch user reviews');
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async fetchVisitReviews(visitId) {
			this.setLoading(true);
			this.setError(null);

			try {
				const response = await api.getReviews();
				// Filter reviews by visit
				const visitReviews = response.data.filter((review) => review.visit?.id === visitId);
				this.setReviews(visitReviews);
				return visitReviews;
			} catch (error) {
				this.setError(error.message || 'Failed to fetch visit reviews');
				throw error;
			} finally {
				this.setLoading(false);
			}
		}
	}
});
